/**
 * CellTypeCatalog.ts
 * Metadata for cell types, themes, and floors — grouped for UI dropdowns.
 */

import type { CellType, CellTheme, FloorConfig, ConnectorMood } from '../types/OctileTypes.ts'

// ─── Cell Types by Category ─────────────────────────────────

export const CELL_TYPES_BY_CATEGORY: Record<string, CellType[]> = {
    'Structural': ['empty', 'passage', 'junction', 'hub', 'stairs_up', 'stairs_down', 'shaft', 'bridge', 'gate', 'secret'],
    'Natural': ['cave', 'stalactite_cave', 'stalagmite_cave', 'crystal_cave', 'mushroom_grotto', 'water_pool', 'underground_stream', 'lava_cell', 'ice_cave', 'collapsed'],
    'Constructed': ['chamber', 'pillared_hall', 'alcove', 'prison', 'library', 'forge'],
    'Sacred': ['shrine', 'entrance', 'boss_throne'],
    'Treasure': ['vault', 'cache', 'hoard'],
    'Hazard': ['trap_floor', 'trap_walls', 'trap_ceiling', 'gauntlet', 'puzzle', 'mimic_den'],
    'Combat': ['guard_post', 'patrol_route', 'ambush', 'nest', 'arena', 'barricade'],
}

// ─── Labels ─────────────────────────────────────────────────

const TYPE_LABELS: Partial<Record<CellType, string>> = {
    empty: 'Empty', passage: 'Passage', junction: 'Junction', hub: 'Hub',
    stairs_up: 'Stairs Up', stairs_down: 'Stairs Down', shaft: 'Shaft',
    bridge: 'Bridge', gate: 'Gate', secret: 'Secret',
    cave: 'Cave', stalactite_cave: 'Stalactite Cave', stalagmite_cave: 'Stalagmite Cave',
    crystal_cave: 'Crystal Cave', mushroom_grotto: 'Mushroom Grotto',
    water_pool: 'Water Pool', underground_stream: 'Underground Stream',
    lava_cell: 'Lava Cell', ice_cave: 'Ice Cave', collapsed: 'Collapsed',
    chamber: 'Chamber', pillared_hall: 'Pillared Hall', alcove: 'Alcove',
    prison: 'Prison', library: 'Library', forge: 'Forge',
    shrine: 'Shrine', entrance: 'Entrance', boss_throne: 'Boss Throne',
    vault: 'Vault', cache: 'Cache', hoard: 'Hoard',
    trap_floor: 'Trap Floor', trap_walls: 'Trap Walls', trap_ceiling: 'Trap Ceiling',
    gauntlet: 'Gauntlet', puzzle: 'Puzzle', mimic_den: 'Mimic Den',
    guard_post: 'Guard Post', patrol_route: 'Patrol Route', ambush: 'Ambush',
    nest: 'Nest', arena: 'Arena', barricade: 'Barricade',
}

const THEME_LABELS: Record<CellTheme, string> = {
    natural_cave: 'Natural Cave',
    ancient_stone: 'Ancient Stone',
    crystal: 'Crystal',
    organic: 'Organic',
    frozen: 'Frozen',
    volcanic: 'Volcanic',
    overgrown: 'Overgrown',
    shadow: 'Shadow',
}

const FLOOR_LABELS: Record<FloorConfig, string> = {
    flat: 'Flat',
    raised_center: 'Raised Center',
    sunken_center: 'Sunken Center',
    split_level: 'Split Level',
    sloped: 'Sloped',
    broken: 'Broken',
}

const MOOD_LABELS: Record<ConnectorMood, string> = {
    tunnel: 'Tunnel',
    passage: 'Passage',
    grand: 'Grand',
    cramped: 'Cramped',
    bridge: 'Bridge',
    flooded: 'Flooded',
    frozen: 'Frozen',
    volcanic: 'Volcanic',
    overgrown: 'Overgrown',
}

// ─── Getters ────────────────────────────────────────────────

export function getCellTypeLabel(type: CellType): string {
    return TYPE_LABELS[type] ?? type.replace(/_/g, ' ')
}

export function getThemeLabel(theme: CellTheme): string {
    return THEME_LABELS[theme]
}

export function getFloorLabel(floor: FloorConfig): string {
    return FLOOR_LABELS[floor]
}

export function getMoodLabel(mood: ConnectorMood): string {
    return MOOD_LABELS[mood]
}

// All themes for dropdown iteration
export const ALL_THEMES: CellTheme[] = Object.keys(THEME_LABELS) as CellTheme[]
export const ALL_FLOORS: FloorConfig[] = Object.keys(FLOOR_LABELS) as FloorConfig[]
export const ALL_MOODS: ConnectorMood[] = Object.keys(MOOD_LABELS) as ConnectorMood[]
